// client/src/api/auth.js
const API_BASE_URL = '/api'; // Your backend API base URL

/**
 * Helper to get headers without authentication token.
 * @returns {object} Headers object.
 */
const getHeaders = () => {
  return {
    'Content-Type': 'application/json'
  };
};

/**
 * Registers a new user.
 * @param {object} userData - Object containing username, email, password, and optional company details.
 * @returns {Promise<object>} A promise that resolves to the registration response.
 */
export const registerUser = async (userData) => {
  const response = await fetch(`${API_BASE_URL}/auth/register`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(userData),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to register user');
  }
  return response.json(); // Backend will set cookie, no token in response
};

/**
 * Logs in a user.
 * @param {string} email - User's email.
 * @param {string} password - User's password.
 * @param {boolean} rememberMe - Whether to extend session to 30 days (optional).
 * @returns {Promise<object>} A promise that resolves to the login response.
 */
export const loginUser = async (email, password, rememberMe = false) => {
  const response = await fetch(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ email, password, rememberMe }),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to log in');
  }
  return response.json(); // Backend will set cookie, no token in response
};

/**
 * Logs out the current user.
 * @returns {Promise<object>} A promise that resolves to the logout response.
 */
export const logoutUser = async () => {
  const response = await fetch(`${API_BASE_URL}/auth/logout`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to log out');
  }
  return response.json();
};

/**
 * Fetches the current user's data.
 * @returns {Promise<object|null>} A promise that resolves to the user data or null if unauthorized.
 */
export const fetchCurrentUser = async () => {
  const response = await fetch(`${API_BASE_URL}/users/me`, {
    method: 'GET',
    headers: getHeaders(),
    credentials: 'include',
  });

  if (response.status === 401) {
    // If unauthorized, return null instead of throwing an error
    return null;
  }

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch user data');
  }
  return response.json();
};


/**
 * Updates the user's onboarding status.
 * @param {string} status - The new onboarding status ('in_progress' or 'completed').
 * @returns {Promise<object>} A promise that resolves to the response.
 */
export const updateOnboardingStatus = async (status) => {
  const response = await fetch(`${API_BASE_URL}/users/onboarding-status`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify({ status }),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to update onboarding status');
  }
  return response.json();
};

/**
 * Updates the current onboarding phase.
 * @param {string} phase - The new onboarding phase ('phase_1', 'phase_2', or 'completed').
 * @returns {Promise<object>} A promise that resolves to the response.
 */
export const updateCurrentOnboardingPhase = async (phase) => {
  const response = await fetch(`${API_BASE_URL}/users/onboarding-phase`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify({ phase }),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to update onboarding phase');
  }
  return response.json();
};

/**
 * Marks Phase 1 completion celebration as shown.
 * @returns {Promise<object>} A promise that resolves to the response.
 */
export const setPhase1CompletionCelebrated = async () => {
  const response = await fetch(`${API_BASE_URL}/users/phase1-completion-celebrated`, {
    method: 'PUT',
    headers: getHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to mark Phase 1 celebration as shown');
  }
  return response.json();
};

/**
 * Updates the current user's profile data.
 * @param {object} userData - Object containing updated user data.
 * @returns {Promise<object>} A promise that resolves to the updated user data.
 */
export const updateProfile = async (userData) => {
  const response = await fetch(`${API_BASE_URL}/users/profile`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(userData),
    credentials: 'include',
  });

  if (!response.ok) {
    let errorMessage = 'Failed to update profile';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      // If JSON parsing fails, use a default message or log the raw response for debugging
      console.error('Error parsing response:', e);
      errorMessage = 'Server returned an unexpected response. Please check the endpoint configuration.';
    }
    throw new Error(errorMessage);
  }
  return response.json();
};